import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { impersonateTenant } from '@/features/impersonation/api';
import { tenantSession } from '@/lib/session';

/**
 * Starts a view-as session: mint → install → open the tenant portal.
 *
 * The two portals' sessions stay strictly separate. The SA's own session is
 * never touched; the tenant token goes into the TENANT store as an *ephemeral*
 * session, so it cannot be refreshed and does not survive a reload.
 *
 * The portal query cache is dropped on the way in: without that, a tenant
 * portal used earlier in this browser would paint its numbers under the
 * impersonated tenant's name until every query refetched.
 */
export function useStartImpersonation() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: impersonateTenant,
    onSuccess: (session) => {
      queryClient.removeQueries({ queryKey: ['portal'] });
      tenantSession.startEphemeral(session.accessToken, session.user);
      navigate('/');
    },
  });
}

/** Ends a view-as session: clears the tenant store only, then back to /admin. */
export function useExitImpersonation(): () => void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useCallback(() => {
    // Only the tenant store is cleared. The SA session lives in a separate store
    // under separate keys, so the operator lands on /admin still signed in —
    // which is the whole point of two independent session stores.
    tenantSession.clear();
    queryClient.removeQueries({ queryKey: ['portal'] });
    navigate('/cp', { replace: true });
  }, [navigate, queryClient]);
}
